import { useEffect, useMemo, useRef, useState } from 'react';
import type { MailAttachment } from '@jerome2/shared';
import { frameDocument, sanitizeMailHtml, textAsHtml } from './html.ts';
import { Button } from '../ui/primitives.tsx';

// De mail zelf in een afgeschermd iframe (sandbox zonder scripts). Het frame blijft onzichtbaar
// tot de eerste meting en heeft tot die tijd een geschatte hoogte, zodat de lezer niet van een
// halve naar een hele pagina springt; daarna volgt de hoogte de inhoud: groter als er nog iets
// binnenkomt (afbeeldingen), kleiner als de inhoud smaller herschikt (telefoon gedraaid, paneel
// versmald). Gemeten wordt de wrapper in het frame (flow-root), niet de body: die hangt niet van de
// framehoogte af, dus krimpen geeft geen terugkoppeling.

/** Geschatte hoogte in pixels op basis van de platte tekst: ongeveer een regel van 24 px per 90 tekens. */
export function estimateHeight(text: string): number {
  const t = text ?? '';
  const lines = Math.ceil(t.length / 90) + (t.match(/\n/g)?.length ?? 0) * 0.5;
  return Math.max(160, Math.min(1400, Math.round(lines * 24) + 24));
}

const FALLBACK_TIMES = [100, 300, 600, 1000, 2000, 3000];

export function HtmlBody(props: { messageId: string; html: string | null; text: string; attachments: MailAttachment[] }) {
  const [showExternal, setShowExternal] = useState(false);
  const [height, setHeight] = useState(() => estimateHeight(props.text));
  const [measured, setMeasured] = useState(false);
  const ref = useRef<HTMLIFrameElement>(null);
  const safe = useMemo(() => (props.html ? sanitizeMailHtml(props.html, props.messageId, props.attachments, { showExternal }) : { html: textAsHtml(props.text), blockedImages: 0 }), [props.html, props.text, props.messageId, props.attachments, showExternal]);
  const doc = useMemo(() => frameDocument(safe.html), [safe.html]);

  useEffect(() => {
    setShowExternal(false);
    setMeasured(false);
    setHeight(estimateHeight(props.text));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.messageId]);

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return;
    let first = true;
    let stopped = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    let observer: ResizeObserver | null = null;
    const wrapper = () => frame.contentDocument?.getElementById('jerome-wrap') ?? null;
    const measure = () => {
      if (stopped) return;
      const wrap = wrapper();
      if (!wrap || !wrap.childNodes.length) return;
      // Hoogte van de inhoud plus de randen van de body (4 + 8 px) en wat lucht; de eerste meting vervangt de schatting.
      const next = Math.max(80, Math.min(20_000, Math.ceil(wrap.getBoundingClientRect().height) + 20));
      setHeight(next);
      if (first) {
        first = false;
        setMeasured(true);
      }
    };
    const watch = () => {
      measure();
      const cd = frame.contentDocument;
      const wrap = wrapper();
      if (!cd || !wrap) return;
      if (typeof ResizeObserver !== 'undefined' && !observer) {
        observer = new ResizeObserver(() => measure());
        observer.observe(wrap);
      }
      for (const img of Array.from(cd.images)) img.addEventListener('load', measure, { once: true });
      for (const t of FALLBACK_TIMES) timers.push(setTimeout(measure, t));
    };
    frame.addEventListener('load', watch);
    if (frame.contentDocument?.readyState === 'complete' && wrapper()?.childNodes.length) watch();
    return () => {
      stopped = true;
      frame.removeEventListener('load', watch);
      observer?.disconnect();
      for (const t of timers) clearTimeout(t);
    };
  }, [doc]);

  return (
    <div>
      {safe.blockedImages > 0 ? (
        <div className="flex items-center gap-3 rounded-md bg-tint px-3 py-2 mb-3 text-[13px] text-ink-soft">
          <span>
            {safe.blockedImages === 1 ? 'Eén externe afbeelding' : `${safe.blockedImages} externe afbeeldingen`} niet geladen.
          </span>
          <Button size="sm" variant="ghost" onClick={() => setShowExternal(true)}>
            Toon afbeeldingen
          </Button>
        </div>
      ) : null}
      <iframe ref={ref} title="Mailinhoud" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" srcDoc={doc} style={{ height, visibility: measured ? 'visible' : 'hidden' }} className="w-full border-0 block" />
    </div>
  );
}
